import { inject, Injectable, isDevMode } from '@angular/core';

import { SuiSidebarService, type SidebarMode } from '@semeris/ui';
import introJs from 'intro.js';

import { GuidedTourEngine, GuidedTourHandlers } from '../models/guided-tour-engine';
import { TourStep } from '../models/tour.model';
import {
  guideTargetSelector,
  isGuideTargetMeasurable,
  resolveGuideTarget,
  resolveGuideTargetBox,
  waitForGuideTarget,
} from '../utils/guide-target';

type IntroInstance = ReturnType<typeof introJs>;

const OPTIONS = {
  showBullets: false,
  showStepNumbers: true,
  disableInteraction: true,
  doneLabel: 'Finish Guided Tour',
  nextLabel: 'Continue',
  prevLabel: 'Back',
  tooltipClass: 'semeris-guided-tour',
  // No colour class: the theme points sui's button variables per button, so Back reads as
  // secondary and Continue as primary — intro.js gives both the same class.
  buttonClass: 'sui-btn sui-btn-sm',
} as const;

/** Interim engine until `SuiGuide` lands (#279); the whole class goes with it. */
@Injectable({ providedIn: 'root' })
export class IntroJsTourEngine implements GuidedTourEngine {
  private readonly sidebar = inject(SuiSidebarService);

  private readonly intro: IntroInstance = introJs();
  private running = false;
  private suspendedSidebarMode: SidebarMode | null = null;
  /** Bumped per start, so a launch still waiting for its target knows it has been superseded. */
  private generation = 0;

  public isActive(): boolean {
    return this.running;
  }

  public start(steps: TourStep[], handlers: GuidedTourHandlers): void {
    // Claimed before the target wait in `launch`, not after: for those seconds the engine is
    // already committed, and a `stop()` or a second `start()` has to be able to see that.
    const generation = ++this.generation;
    const wasRunning = this.running;
    this.running = true;

    // One shared intro.js instance, so a replacement has to wait for the outgoing tour's
    // teardown: it runs behind a transition-matched timeout and would otherwise fire midway
    // through the new tour and strip its layers. Its hooks are still the old tour's here, so
    // configuring the new one before the exit settles would also hand the old exit the new
    // tour's handlers — and clear the query params of the tour just starting.
    if (wasRunning) {
      void this.intro.exit(true).then(() => this.launch(steps, handlers, generation));
      return;
    }

    void this.launch(steps, handlers, generation);
  }

  public stop(): void {
    if (!this.running) {
      return;
    }

    this.running = false;
    // Also abandons a launch still waiting for its target — it has no layers to exit yet.
    this.generation++;
    // `exit` fires `onexit`, which restores the sidebar and runs the tour's own exit handler.
    void this.intro.exit(true);
  }

  private async launch(steps: TourStep[], handlers: GuidedTourHandlers, generation: number): Promise<void> {
    // intro.js resolves every step's element ONCE, when `start()` fetches them, and substitutes
    // a centred 0x0 placeholder for anything missing. Since the dim is a 5000px spread shadow on
    // a helper layer sized to the target, a 0x0 placeholder dims the whole screen and spotlights
    // nothing — and no later `refresh()` recovers it, because the item is read before the step's
    // own hook runs. So the fetch must not happen until the page has its targets: wait on the
    // first step that has one (the opening step usually does not).
    await waitForGuideTarget(steps.find((step) => step.target)?.target);

    // Stopped, or replaced by a later tour, while the wait was outstanding.
    if (generation !== this.generation) {
      return;
    }

    // Re-asserted: on the replacement path the outgoing tour's `onexit` cleared it after
    // `start` claimed the engine.
    this.running = true;
    this.suspendFloatingSidebar();

    void this.intro
      .setOptions({ ...OPTIONS, steps: steps.map(toIntroStep) })
      // `_currentStep` and `_options` are intro.js internals; the exact version pin is what
      // makes that safe, so a bump needs this hook re-checked.
      .onbeforechange(async () => {
        const index = this.intro._currentStep;
        const upcoming = steps[index];
        await waitForGuideTarget(upcoming?.target);

        // A replacement only swaps the registered hook, so one already awaiting resumes against
        // whatever `_options` now holds — writing this tour's element into the next tour's step
        // at the same index, and refreshing an instance it no longer owns.
        if (generation !== this.generation) {
          return true;
        }

        reportMissingTarget(upcoming);

        // Re-point the step now the target has settled: it may only just have mounted, or may
        // have needed the ancestor fallback, neither of which was knowable at fetch time.
        const option = this.intro._options.steps[index];
        if (option && upcoming?.target) {
          option.element = elementForTarget(upcoming.target);
        }

        // `refresh` dereferences the overlay layers, which do not exist before the first step.
        if (document.querySelector('.introjs-helperLayer')) {
          this.intro.refresh(true);
        }

        return true;
      })
      // `reason` is 'skip' when the user dismissed — completing on that would tick the row
      // and write the profile for a tour they never saw.
      .oncomplete((_step, reason) => {
        if (reason !== 'skip') {
          handlers.onComplete();
        }
      })
      .onexit(() => {
        this.running = false;
        this.restoreSidebar();

        // Superseded tours must not touch the URL — it already carries the incoming tour's id,
        // and `onExit` strips it. `oncomplete` needs no such guard: `exit(true)` never fires it,
        // so it only ever runs for a tour the user actually finished.
        if (generation === this.generation) {
          handlers.onExit();
        }
      })
      .start();
  }

  /** A floating sidebar renders in the top layer, where no overlay can highlight it. */
  private suspendFloatingSidebar(): void {
    if (this.sidebar.mode() !== 'float') {
      return;
    }

    this.suspendedSidebarMode = 'float';
    this.sidebar.mode.set('expanded');
  }

  private restoreSidebar(): void {
    if (!this.suspendedSidebarMode) {
      return;
    }

    this.sidebar.mode.set(this.suspendedSidebarMode);
    this.suspendedSidebarMode = null;
  }
}

function toIntroStep(step: TourStep) {
  return {
    title: step.title,
    // Step bodies are HTML; `sui-prose` is the stylesheet that already renders that vocabulary.
    intro: `<div class="sui-prose sui-prose-sm">${step.intro}</div>`,
    position: step.position,
    ...(step.target ? { element: elementForTarget(step.target) } : {}),
  };
}

/**
 * A selector wherever possible — intro.js re-resolves strings on every fetch, resolved nodes
 * never — and a concrete node only when the target itself has no box and an ancestor stands in
 * for it, which a selector cannot express.
 */
function elementForTarget(target: string): string | HTMLElement {
  const box = resolveGuideTargetBox(target);

  return box && box !== resolveGuideTarget(target) ? box : guideTargetSelector(target);
}

/** Reported per step, after its wait — a start-time sweep would flag targets that do arrive. */
function reportMissingTarget(step: TourStep | undefined): void {
  if (!isDevMode() || !step?.target) {
    return;
  }

  const element = resolveGuideTarget(step.target);

  if (!element) {
    console.warn(`[guided-tour] Guide target never appeared: ${step.target}`);
  } else if (!isGuideTargetMeasurable(step.target)) {
    // Names the anchor to move: the element is there, but it has no box to cut out of the dim.
    const { width, height } = element.getBoundingClientRect();
    console.warn(
      `[guided-tour] Guide target "${step.target}" has no measurable box (${width}x${height}) — ` +
        `the step will dim the page without a spotlight.`,
      element,
    );
  }
}
